import type { User } from '@/scenes/authentication/types/types.auth';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

interface AdminEditUserModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (userData: EditUserData) => void;
}

interface EditUserData {
  id?: any;
  firstName?: string;
  lastName?: string;
  email?: string;
  referer?: string;
  source?: string;
  positionTitle?: string;
  company?: string;
  subscriptionPlan?: string;
  landingPage?: string;
  campaign?: string;
}

interface Plan {
  slug: string;
  name: string;
  features?: any;
}

const SOURCE_OPTIONS = [
  { value: 'google_search', label: 'Google Search' },
  { value: 'linkedin_feed', label: 'LinkedIn - Feed / Post' },
  { value: 'linkedin_message', label: 'LinkedIn - Message' },
  { value: 'referral', label: 'Referral from a colleague or friend' },
  { value: 'advertisement', label: 'Advertisement (Google, LinkedIn, etc.)' },
  { value: 'other', label: 'Other' },
];

function AdminEditUserModal({
  isOpen,
  user,
  onClose,
  onSave,
}: AdminEditUserModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    subscriptionPlan: '',
    referer: '',
    source: '',
    positionTitle: '',
    landingPage: '',
    campaign: '',
  });
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        company: user.accountMemberships?.[0]?.account?.name || '',
        subscriptionPlan:
          user.accountMemberships?.[0]?.account?.subscriptions?.[0]?.planSlug ||
          'free',
        referer: user.referer || '',
        source: user.source || '',
        positionTitle: user.positionTitle || '',
        landingPage: user.landingPage || '',
        campaign: user.campaign || '',
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/plan`
        );
        setPlans(response.data);
      } catch (error) {
      } finally {
        setLoadingPlans(false);
      }
    };

    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    const userData: EditUserData = {
      id: user?.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      referer: formData.referer,
      source: formData.source,
      positionTitle: formData.positionTitle,
      company: formData.company,
      subscriptionPlan: formData.subscriptionPlan,
      landingPage: formData.landingPage,
      campaign: formData.campaign,
    };

    onSave(userData);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginTop: '8px',
          }}
        >
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            fullWidth
            disabled
          />
          <TextField
            label="Company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            fullWidth
          />
          <TextField
            label="Position Title"
            value={formData.positionTitle}
            onChange={(e) => handleInputChange('positionTitle', e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Subscription Plan</InputLabel>
            <Select
              value={formData.subscriptionPlan}
              label="Subscription Plan"
              onChange={(e) =>
                handleInputChange('subscriptionPlan', e.target.value)
              }
              disabled={loadingPlans}
            >
              {loadingPlans ? (
                <MenuItem disabled>
                  <em>Loading plans...</em>
                </MenuItem>
              ) : (
                plans.map((plan) => (
                  <MenuItem key={plan.slug} value={plan.slug}>
                    {plan.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            label="Referrer"
            value={formData.referer}
            onChange={(e) => handleInputChange('referer', e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={formData.source}
              label="Source"
              onChange={(e) => handleInputChange('source', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Landing Page"
            value={formData.landingPage}
            onChange={(e) => handleInputChange('landingPage', e.target.value)}
            fullWidth
          />
          <TextField
            label="Campaign"
            value={formData.campaign}
            onChange={(e) => handleInputChange('campaign', e.target.value)}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdminEditUserModal;
